import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';
import { t } from '../utils/i18n.js';

/**
 * REVIEWS — Reseñas con estrellas + microdata schema.org.
 *
 * Prueba social con estrellas SVG y datos estructurados INLINE (microdata:
 * itemscope/itemprop). Se usa microdata y NO <script type="application/ld+json">
 * a propósito: el saneador del HTML publicado (core_services/html_sanitizer.py)
 * BORRA los <script>, pero PRESERVA los atributos itemscope/itemprop/itemtype/content
 * (no son atributos on* ni URL) → la marca de datos estructurados sobrevive a la publicación y
 * Google puede pintar las estrellas en el resultado de búsqueda.
 *
 * Estructura schema.org:
 *   <section itemscope itemtype=".../Product">          ← lo reseñado (name editable)
 *     itemprop="aggregateRating" → AggregateRating (ratingValue, reviewCount editables)
 *     itemprop="review" (×N)     → Review (author/Person, reviewRating/Rating, reviewBody)
 *
 * Todo el contenido es del cliente/IA (nada hardcodeado): subject/título/valoración
 * media/nº de reseñas y cada autor+texto son editables inline (data-field). El
 * ratingValue de cada reseña viaja como atributo `content` (preservado por el
 * extractor al no ser data-field) → estrellas coherentes con el dato estructurado.
 *
 * Modelo de datos (props):
 *   { subject?, title?, subtitle?, rating?, rating_count?, source?,
 *     reviews:[{ author, author_url?, role?, text, rating? }] }
 *
 * ORIGEN DE LOS DATOS (props.source, lo fija el panel de inserción):
 *   - ausente        → las escribe el cliente a mano (placeholders editables).
 *   - {mode:'places'} → vienen de la ficha de Google del negocio (core_services/places.py) y
 *     el servidor las refresca al servir la página (views.py::_refrescar_resenas_places).
 *     De ahí salen dos obligaciones del ToS de Google que se cumplen aquí:
 *       · ATRIBUCIÓN: cada reseña enlaza el perfil de su autor (`author_url`). El avatar
 *         (profile_photo_url) queda fuera: block-meta declara supportsImage:false para este
 *         bloque, así que el bloque no tiene canal de imagen.
 *       · NO TRADUCIR: `reviews` y `subject` se excluyen del traductor cuando el origen es
 *         Places (js/utils/translatable.js::SOURCE_LOCKED, compartido con el backend).
 *
 * Coordinado con g-seo-cwv (que añade JSON-LD complementario en el <head>): aquí el
 * canal es microdata visible en el cuerpo; ambos son compatibles y se refuerzan.
 *
 * Variantes (CSS-driven, clases `.rv-*` compartidas → DRY):
 *   v1 = Tarjetas: cabecera con nota media + grid de tarjetas de reseña.
 *   v2 = Lista: cabecera + reseñas apiladas en filas separadas por hairline.
 */

// Estrella rellena / vacía (SVG inline; heredan color por currentColor).
const STAR_FULL = (size) => `<svg class="rv-star rv-star-full" viewBox="0 0 24 24" width="${size}" height="${size}" fill="currentColor" aria-hidden="true"><path d="M12 2.2l2.95 5.98 6.6.96-4.77 4.65 1.13 6.57L12 17.98l-5.9 3.1 1.13-6.57L2.46 9.14l6.6-.96z"/></svg>`;
const STAR_EMPTY = (size) => `<svg class="rv-star rv-star-empty" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true"><path d="M12 2.2l2.95 5.98 6.6.96-4.77 4.65 1.13 6.57L12 17.98l-5.9 3.1 1.13-6.57L2.46 9.14l6.6-.96z"/></svg>`;

// Enlace de ATRIBUCIÓN (perfil del autor en Google). El author_url ya viene validado del
// servidor (places.py::_url), pero el render es el último punto antes del href y las props
// pueden venir de un JSON viejo o tocado a mano → se revalida aquí (https estricto).
const AUTHOR_URL_OK = /^https:\/\/[^\s"'<>\\]+$/;
const authorLink = (r) => {
    const url = (r.author_url || '').trim();
    if (!AUTHOR_URL_OK.test(url)) return '';
    // itemprop="url" dentro del Person → la atribución también viaja en la microdata.
    // rel="nofollow ugc": es contenido de terceros, no un enlace editorial del cliente.
    return `<a class="rv-author-link" href="${url}" itemprop="url" target="_blank" rel="noopener nofollow ugc">${t('review_src')}</a>`;
};

// Fila de 5 estrellas: llenas hasta round(value). value se clampa a [0,5].
const starsRow = (value, size = 18) => {
    const v = Math.max(0, Math.min(5, Number(value) || 0));
    const full = Math.round(v);
    let out = '<span class="rv-stars" aria-hidden="true">';
    for (let i = 0; i < 5; i++) out += i < full ? STAR_FULL(size) : STAR_EMPTY(size);
    return out + '</span>';
};

export const reviewsBlock = (rawProps, index = 0) => {
    const p = safe(rawProps);
    const variant = p.variant || 'v1';
    // safe() no normaliza p.reviews → default aquí.
    const reviews = Array.isArray(p.reviews) ? p.reviews : [];

    const subject = p.subject || '';
    const title = p.title || 'Lo que dicen nuestros clientes';
    const subtitle = p.subtitle || '';
    // Nota media y recuento: derivados si el cliente no los fija (media real de las reseñas).
    const ratingsNums = reviews.map((r) => Number(r.rating)).filter((n) => !isNaN(n) && n > 0);
    const avg = ratingsNums.length ? (ratingsNums.reduce((a, b) => a + b, 0) / ratingsNums.length) : 5;
    const rating = (p.rating != null && p.rating !== '') ? p.rating : avg.toFixed(1);
    const ratingCount = (p.rating_count != null && p.rating_count !== '') ? p.rating_count : String(reviews.length);

    const isDark = getLuminance(...hexToRgb(p.bgColor)) < 0.179;
    const btnBg = isDark ? '#ffffff' : '#111827';
    const btnIcon = isDark ? '#111827' : '#ffffff';

    const itemToolbar = (i) => `
        ${i !== 0 ? `<button class="item-add-btn card-add-left" data-array="reviews" data-index="${i}">+</button>` : ''}
        <button class="item-add-btn card-add-right" data-array="reviews" data-index="${i}">+</button>
        <div class="item-tools">
            <button class="tool-btn item-drag-handle" title="Mover" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.drag}</button>
            <button class="tool-btn item-delete-btn" data-array="reviews" data-index="${i}" title="Borrar" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.trash}</button>
        </div>`;

    // Cabecera con AggregateRating (nota media + estrellas + nº de reseñas), todo editable.
    const header = `
        <div class="rv-head">
            ${subject ? `<span class="rv-subject" itemprop="name" data-field="subject" contenteditable="true">${subject}</span>`
                      : `<span class="rv-subject rv-subject-empty" itemprop="name" data-field="subject" contenteditable="true"></span>`}
            <h2 class="rv-title" data-field="title" contenteditable="true">${title}</h2>
            ${subtitle ? `<p class="rv-subtitle" data-field="subtitle" contenteditable="true">${subtitle}</p>` : ''}
            <div class="rv-aggregate" itemprop="aggregateRating" itemscope itemtype="https://schema.org/AggregateRating">
                <span class="rv-avg" itemprop="ratingValue" data-field="rating" contenteditable="true">${rating}</span>
                <span class="rv-outof">/ 5</span>
                ${starsRow(rating, 20)}
                <span class="rv-count">(<span itemprop="reviewCount" data-field="rating_count" contenteditable="true">${ratingCount}</span>)</span>
                <span itemprop="bestRating" content="5" hidden></span>
                <span itemprop="worstRating" content="1" hidden></span>
            </div>
        </div>`;

    const cardAttrs = (i) => `data-item-index="${i}" data-array-name="reviews" data-array="reviews" data-index="${i}" style="--stagger-i:${i};"`;

    // Contenido de una reseña (microdata Review). El ratingValue por reseña va en
    // `content` (no editable inline; el extractor lo preserva) → estrellas y dato coinciden.
    const reviewInner = (r, i) => {
        const rv = (r.rating != null && r.rating !== '') ? r.rating : 5;
        return `
        ${itemToolbar(i)}
        <div class="rv-card-top" itemprop="reviewRating" itemscope itemtype="https://schema.org/Rating">
            ${starsRow(rv, 16)}
            <span itemprop="ratingValue" content="${rv}" hidden></span>
            <span itemprop="bestRating" content="5" hidden></span>
        </div>
        <p class="rv-text" itemprop="reviewBody" data-field="text" contenteditable="true">${r.text || ''}</p>
        <div class="rv-author" itemprop="author" itemscope itemtype="https://schema.org/Person">
            <span class="rv-name" itemprop="name" data-field="author" contenteditable="true">${r.author || ''}</span>
            ${r.role !== undefined ? `<span class="rv-role" data-field="role" contenteditable="true">${r.role || ''}</span>` : `<span class="rv-role" data-field="role" contenteditable="true"></span>`}
            ${authorLink(r)}
        </div>`;
    };

    const baseStyle = `--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --accent-contrast:${p.accentContrast}; --font:'${p.fontFamily || "Inter"}'; padding: 100px 8%;`;

    // itemtype Product a nivel de sección: ancla del aggregateRating + reviews.
    const sectionOpen = `<section id="bloque-reviews-${index}" class="reveal variant-${variant} rv-section" style="${baseStyle}" itemscope itemtype="https://schema.org/Product">`;

    // ═══════════════════════════════════════════
    // v2 — Lista: reseñas apiladas en filas
    // ═══════════════════════════════════════════
    if (variant === 'v2') {
        return `
    ${sectionOpen}
        ${header}
        <div class="rv-list">
            ${reviews.map((r, i) => `
            <div class="rv-card rv-row sortable-item stagger-item" ${cardAttrs(i)} itemprop="review" itemscope itemtype="https://schema.org/Review">
                ${reviewInner(r, i)}
            </div>`).join('')}
        </div>
    </section>`;
    }

    // ═══════════════════════════════════════════
    // v1 (default) — Tarjetas: grid de reseñas
    // ═══════════════════════════════════════════
    return `
    ${sectionOpen}
        ${header}
        <div class="rv-grid">
            ${reviews.map((r, i) => `
            <div class="rv-card sortable-item stagger-item" ${cardAttrs(i)} itemprop="review" itemscope itemtype="https://schema.org/Review">
                ${reviewInner(r, i)}
            </div>`).join('')}
        </div>
    </section>`;
};
